import { Calendar, MapPin, Edit, Trash2, Users, Ticket, ExternalLink, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { Event } from '@/lib/api/types';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { PermissionGuard } from '@/components/guards/PermissionGuard';
import { isEventPast, parseEventDate } from '@/lib/utils/events';

interface EventListItemProps {
  event: Event;
  onEdit: (event: Event) => void;
  onDelete: (event: Event) => void;
  onViewRSVPs: (event: Event) => void;
}

export function EventListItem({ event, onEdit, onDelete, onViewRSVPs }: EventListItemProps) {
  const rsvpCount = event.rsvpCount ?? 0;
  const isPastEvent = isEventPast(event.date);
  const seatsLeft = Math.max(event.seatsAvailable - rsvpCount, 0);
  const fillPercent = event.seatsAvailable > 0 ? Math.min((rsvpCount / event.seatsAvailable) * 100, 100) : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
      <Card className="overflow-hidden rounded-lg border-border/50 transition-shadow duration-200 hover:shadow-md">
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row">
            {/* Cover Image */}
            <div className="relative h-40 w-full flex-shrink-0 sm:h-auto sm:w-48">
              {event.imageUrl ? (
                <img
                  src={event.imageUrl}
                  alt=""
                  loading="lazy"
                  className={`h-full w-full object-cover ${isPastEvent ? 'grayscale' : ''}`}
                />
              ) : (
                <div className="flex h-full min-h-[8rem] w-full items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                  <Calendar className="h-10 w-10 text-primary/40" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col gap-4 p-5">
              <div>
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h3 className="line-clamp-1 text-lg font-semibold text-foreground">{event.title}</h3>
                  {isPastEvent ? (
                    <Badge variant="secondary" className="text-xs">Past</Badge>
                  ) : seatsLeft === 0 ? (
                    <Badge variant="outline" className="border-red-600 text-xs text-red-600">Fully booked</Badge>
                  ) : seatsLeft <= 5 ? (
                    <Badge variant="outline" className="border-orange-600 text-xs text-orange-600">
                      {seatsLeft} seat{seatsLeft === 1 ? '' : 's'} left
                    </Badge>
                  ) : null}
                </div>
                <p className="line-clamp-2 text-sm text-muted-foreground">{event.description}</p>
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {format(parseEventDate(event.date), 'EEE, MMM d, yyyy')}
                </span>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {event.location}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Ticket className="h-3.5 w-3.5" />
                  {event.fee > 0 ? `KES ${event.fee.toLocaleString()}` : 'Free'}
                </span>
              </div>

              {/* Capacity */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Users className="h-3.5 w-3.5" />
                    <span className="font-medium tabular-nums text-foreground">{rsvpCount}</span> of{' '}
                    <span className="tabular-nums">{event.seatsAvailable}</span> seats taken
                  </span>
                  <span className="tabular-nums text-muted-foreground">
                    {event.fee > 0 && (
                      <span className="mr-2 text-foreground">
                        KES {(event.amountCollected ?? 0).toLocaleString('en-KE')} collected ·
                      </span>
                    )}
                    {Math.round(fillPercent)}%
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full rounded-full ${
                      fillPercent >= 100 ? 'bg-red-500' : fillPercent >= 80 ? 'bg-orange-500' : 'bg-primary'
                    }`}
                    style={{ width: `${fillPercent}%` }}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => onViewRSVPs(event)} className="h-8">
                  <Users className="mr-1.5 h-3.5 w-3.5" />
                  View RSVPs
                  <span className="ml-1.5 rounded bg-primary-foreground/20 px-1.5 text-xs tabular-nums">
                    {rsvpCount}
                  </span>
                  <ChevronRight className="ml-1 h-3.5 w-3.5" />
                </Button>
                {event.googleMapsLink && (
                  <Button variant="outline" size="sm" asChild className="h-8">
                    <a href={event.googleMapsLink} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                      Map
                    </a>
                  </Button>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <PermissionGuard resource="events" action="update">
                    <Button variant="outline" size="sm" onClick={() => onEdit(event)} className="h-8">
                      <Edit className="mr-1.5 h-3.5 w-3.5" />
                      Edit
                    </Button>
                  </PermissionGuard>
                  <PermissionGuard resource="events" action="delete">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(event)}
                      className="h-8 text-red-600 hover:text-red-700"
                      aria-label={`Delete ${event.title}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </PermissionGuard>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
